/**
 * PACER-related models and DTOs
 * These models are used for PACER plugin status and subscription management
 */

/**
 * Status values for dependent subscriptions.
 * Matches backend DependentSubscriptionStatus enum.
 */
export enum PacerSubscriptionStatus {
  ACTIVE = 'Active',
  CANCELLED = 'Cancelled',
  SUSPENDED = 'Suspended'
}

/**
 * Monthly PACER usage history.
 * Contains aggregated usage data for a specific month.
 */
export interface PacerMonthlyUsage {
  /** The year and month in YYYY-MM format (e.g., "2024-01") */
  yearMonth: string;
  /** Total number of PACER searches performed during this month */
  searchCount: number;
  /** Number of searches that exceeded the included amount */
  overageCount: number;
  /** Cost of overage searches in dollars */
  overageCost: number;
}

/**
 * PACER usage information for the current billing period.
 * Contains current billing period usage data and historical usage information.
 */
export interface PacerUsage {
  /** Total number of PACER searches performed in the current billing period */
  currentPeriodSearches: number;
  /** Number of searches included in the base subscription price (default 100) */
  includedSearches: number;
  /** Number of searches that exceeded the included amount */
  overageSearches: number;
  /** Estimated cost of overage searches in dollars */
  overageCost: number;
  /** Start date of the current billing period (ISO string) */
  billingPeriodStart: string;
  /** End date of the current billing period (ISO string) */
  billingPeriodEnd: string;
  /** Historical monthly usage data */
  monthlyHistory: PacerMonthlyUsage[];
}

/**
 * PACER plugin status including add-on subscription status and usage.
 * Used by the PACER plugin page to display the current subscription status
 * and usage statistics when the add-on is active.
 */
export interface PacerPluginStatus {
  /** Whether the PACER add-on subscription is currently active */
  addonActive: boolean;
  /** The current status of the PACER subscription (Active, Cancelled, Suspended, or null) */
  subscriptionStatus: PacerSubscriptionStatus | null;
  /** Usage statistics for the current billing period (only populated when add-on is active) */
  usage: PacerUsage | null;
}
